import { photoStorage } from '../services/photoStorage';
import { persistor } from '../redux/store';

/**
 * Storage management utilities for monitoring and managing app storage
 */

export interface StorageInfo {
  localStorage: {
    used: number;
    available: number;
    percentage: number;
    items: number;
  };
  sessionStorage: {
    used: number;
    items: number;
  };
  indexedDB: {
    used: number;
    quota: number;
    percentage: number;
  };
  total: {
    used: number;
    quota: number;
    percentage: number;
  };
}

export interface StorageItem {
  key: string;
  size: number;
  type: 'localStorage' | 'sessionStorage';
}

class StorageManager {
  /**
   * Calculate the size of a string in bytes
   */
  private getByteSize(str: string): number {
    return new Blob([str]).size;
  }

  /**
   * Get detailed storage information
   */
  async getStorageInfo(): Promise<StorageInfo> {
    const localStorageInfo = this.getLocalStorageInfo();
    const sessionStorageInfo = this.getSessionStorageInfo();
    const indexedDBInfo = await this.getIndexedDBInfo();
    
    const totalUsed = localStorageInfo.used + sessionStorageInfo.used + indexedDBInfo.used;
    const totalQuota = indexedDBInfo.quota; // Total browser quota
    
    return {
      localStorage: localStorageInfo,
      sessionStorage: sessionStorageInfo,
      indexedDB: indexedDBInfo,
      total: {
        used: totalUsed,
        quota: totalQuota,
        percentage: totalQuota > 0 ? (totalUsed / totalQuota) * 100 : 0
      }
    };
  }

  /**
   * Get localStorage information
   */
  private getLocalStorageInfo(): StorageInfo['localStorage'] {
    let used = 0;
    let items = 0;
    
    try {
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          const value = localStorage.getItem(key) || '';
          used += this.getByteSize(key) + this.getByteSize(value);
          items++;
        }
      }
    } catch (error) {
      console.error('Error calculating localStorage size:', error);
    }
    
    // Estimate available space (typically 5-10MB for localStorage)
    const estimatedQuota = 5 * 1024 * 1024; // 5MB
    const available = Math.max(0, estimatedQuota - used);
    
    return {
      used,
      available,
      percentage: (used / estimatedQuota) * 100,
      items
    };
  }

  /**
   * Get sessionStorage information
   */
  private getSessionStorageInfo(): StorageInfo['sessionStorage'] {
    let used = 0;
    let items = 0;
    
    try {
      for (const key in sessionStorage) {
        if (sessionStorage.hasOwnProperty(key)) {
          const value = sessionStorage.getItem(key) || '';
          used += this.getByteSize(key) + this.getByteSize(value);
          items++;
        }
      }
    } catch (error) {
      console.error('Error calculating sessionStorage size:', error);
    }
    
    return { used, items };
  }

  /**
   * Get IndexedDB information
   */
  private async getIndexedDBInfo(): Promise<StorageInfo['indexedDB']> {
    try {
      const info = await photoStorage.getStorageInfo();
      return {
        used: info.usage,
        quota: info.quota,
        percentage: info.quota > 0 ? (info.usage / info.quota) * 100 : 0
      };
    } catch (error) {
      console.error('Error getting IndexedDB info:', error);
      return { used: 0, quota: 0, percentage: 0 };
    }
  }

  /**
   * Get all storage items with their sizes
   */
  getStorageItems(): StorageItem[] {
    const items: StorageItem[] = [];
    
    // Get localStorage items
    try {
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          const value = localStorage.getItem(key) || '';
          items.push({
            key,
            size: this.getByteSize(key) + this.getByteSize(value),
            type: 'localStorage'
          });
        }
      }
    } catch (error) {
      console.error('Error getting localStorage items:', error);
    }
    
    // Get sessionStorage items
    try {
      for (const key in sessionStorage) {
        if (sessionStorage.hasOwnProperty(key)) {
          const value = sessionStorage.getItem(key) || '';
          items.push({
            key,
            size: this.getByteSize(key) + this.getByteSize(value),
            type: 'sessionStorage'
          });
        }
      }
    } catch (error) {
      console.error('Error getting sessionStorage items:', error);
    }
    
    // Sort by size descending
    return items.sort((a, b) => b.size - a.size);
  }

  /**
   * Clear specific storage types
   */
  async clearStorage(types: {
    localStorage?: boolean;
    sessionStorage?: boolean;
    indexedDB?: boolean;
    redux?: boolean;
  }): Promise<void> {
    if (types.localStorage) {
      try {
        // Preserve auth data if needed
        const authToken = localStorage.getItem('auth_token');
        const userData = localStorage.getItem('user_data');
        const tokenExpiry = localStorage.getItem('token_expiry');
        
        localStorage.clear();
        
        // Restore auth data
        if (authToken) localStorage.setItem('auth_token', authToken);
        if (userData) localStorage.setItem('user_data', userData);
        if (tokenExpiry) localStorage.setItem('token_expiry', tokenExpiry);
      } catch (error) {
        console.error('Error clearing localStorage:', error);
      }
    }
    
    if (types.sessionStorage) {
      try {
        sessionStorage.clear();
      } catch (error) {
        console.error('Error clearing sessionStorage:', error);
      }
    }
    
    if (types.indexedDB) {
      try {
        await photoStorage.clearAll();
      } catch (error) {
        console.error('Error clearing IndexedDB:', error);
      }
    }
    
    if (types.redux) {
      try {
        await persistor.purge();
      } catch (error) {
        console.error('Error purging Redux persist:', error);
      }
    }
  }

  /**
   * Clear old incident data
   */
  async clearOldIncidents(daysToKeep: number = 7): Promise<number> {
    const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);
    let clearedCount = 0;
    
    // Clear old photos from IndexedDB
    try {
      clearedCount = await photoStorage.cleanupOldPhotos(daysToKeep);
    } catch (error) {
      console.error('Error clearing old photos:', error);
    }
    
    // Clear old localStorage items
    try {
      const keys = Object.keys(localStorage);
      for (const key of keys) {
        if (key.includes('incident_') || key.includes('history_')) {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              const data = JSON.parse(value);
              if (data.timestamp && data.timestamp < cutoffTime) {
                localStorage.removeItem(key);
                clearedCount++;
              }
            } catch {
              // Not JSON or doesn't have timestamp, skip
            }
          }
        }
      }
    } catch (error) {
      console.error('Error clearing old localStorage items:', error);
    }
    
    return clearedCount;
  }

  /**
   * Export storage data for debugging
   */
  async exportStorageData(): Promise<string> {
    const data: any = {
      timestamp: new Date().toISOString(),
      localStorage: {},
      sessionStorage: {},
      storageInfo: await this.getStorageInfo()
    };
    
    // Export localStorage
    try {
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          data.localStorage[key] = localStorage.getItem(key);
        }
      }
    } catch (error) {
      data.errors = data.errors || {};
      data.errors.localStorage = error;
    }
    
    // Export sessionStorage
    try {
      for (const key in sessionStorage) {
        if (sessionStorage.hasOwnProperty(key)) {
          data.sessionStorage[key] = sessionStorage.getItem(key);
        }
      }
    } catch (error) {
      data.errors = data.errors || {};
      data.errors.sessionStorage = error;
    }
    
    return JSON.stringify(data, null, 2);
  }

  /**
   * Check if storage is nearly full
   */
  async isStorageNearlyFull(threshold: number = 0.9): Promise<boolean> {
    const info = await this.getStorageInfo();
    return info.total.percentage > (threshold * 100);
  }

  /**
   * Automatic cleanup when storage is nearly full
   */
  async autoCleanup(): Promise<boolean> {
    const isNearlyFull = await this.isStorageNearlyFull(0.85);
    
    if (isNearlyFull) {
      // First try clearing old incidents
      const cleared = await this.clearOldIncidents(3);
      
      if (cleared > 0) {
        console.log(`Auto-cleanup: Cleared ${cleared} old items`);
        return true;
      }
      
      // If still full, clear non-essential data
      const stillFull = await this.isStorageNearlyFull(0.85);
      if (stillFull) {
        await this.clearStorage({
          sessionStorage: true,
          indexedDB: true
        });
        console.log('Auto-cleanup: Cleared session and photo storage');
        return true;
      }
    }
    
    return false;
  }

  /**
   * Format bytes to human-readable string
   */
  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get storage summary as formatted string
   */
  async getStorageSummary(): Promise<string> {
    const info = await this.getStorageInfo();
    
    return `
Storage Usage Summary:
----------------------
LocalStorage: ${this.formatBytes(info.localStorage.used)} (${info.localStorage.percentage.toFixed(1)}%)
SessionStorage: ${this.formatBytes(info.sessionStorage.used)}
IndexedDB: ${this.formatBytes(info.indexedDB.used)} (${info.indexedDB.percentage.toFixed(1)}%)
----------------------
Total: ${this.formatBytes(info.total.used)} / ${this.formatBytes(info.total.quota)} (${info.total.percentage.toFixed(1)}%)
    `.trim();
  }
}

// Export singleton instance
export const storageManager = new StorageManager();

// Export utility functions
export const {
  getStorageInfo,
  getStorageItems,
  clearStorage,
  clearOldIncidents,
  exportStorageData,
  isStorageNearlyFull,
  autoCleanup,
  formatBytes,
  getStorageSummary
} = storageManager;